import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowUpDown, Plus, X, ChevronUp, ChevronDown, ListOrdered } from "lucide-react";
import { SORT_DIRECTIONS, SORT_NULLS, sortCandidates, sortLevels } from "@/lib/sortEngine";
import { smartSortValues } from "@/lib/filterValues";
import api from "@/lib/api";

// Item 29: the universal sort panel — works for every data widget (charts, tables,
// pivots, KPIs, maps) because it writes the semantic config.sort levels.
export function SortEngineEditor({ config, patchConfig, datasetId }) {
  const levels = sortLevels(config);
  const cands = sortCandidates(config);
  const [customAt, setCustomAt] = useState(null);
  if (!cands.length && !levels.length) return null;

  const setLevels = (arr) => patchConfig({ sort: arr });
  const patchAt = (i, patch) => setLevels(levels.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const add = () => {
    const used = new Set(levels.map((l) => l.key));
    const next = cands.find((c) => !used.has(c.key)) || cands[0];
    if (next) setLevels([...levels, { key: next.key, direction: "desc", nulls: "last" }]);
  };
  const move = (i, d) => {
    const to = i + d;
    if (to < 0 || to >= levels.length) return;
    const a = [...levels];
    [a[i], a[to]] = [a[to], a[i]];
    setLevels(a);
  };
  const labelOf = (k) => (cands.find((c) => c.key === k) || {}).label || k;

  return (
    <div data-testid="sort-engine-editor" className="pt-1 border-t border-[color:var(--glass-border)]">
      <div className="flex items-center justify-between gap-2 py-1">
        <Label className="text-sm text-[color:var(--ink-2)] flex items-center gap-1"><ArrowUpDown className="w-3.5 h-3.5" />Sorting</Label>
        <Button data-testid="sort-add-level" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={add}>
          <Plus className="w-3.5 h-3.5 mr-1" />Add level
        </Button>
      </div>
      {!levels.length
        ? <p className="text-[10px] text-[color:var(--ink-3)]">Default order: highest value of the first measure.</p>
        : <p className="text-[10px] text-[color:var(--ink-3)]">These levels drive the visual, its exports and its underlying data — they override the chart's own Sort option.</p>}
      {levels.map((l, i) => (
        <div key={i} data-testid={`sort-level-${i}`} className="mt-1.5 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2 space-y-1.5">
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-[color:var(--ink-3)] w-10 shrink-0">{i === 0 ? "Sort by" : "then by"}</span>
            <Select value={l.key} onValueChange={(v) => patchAt(i, { key: v, custom: null })}>
              <SelectTrigger data-testid={`sort-field-${i}`} className="bg-[color:var(--surface)] flex-1 min-w-0 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cands.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                {!cands.some((c) => c.key === l.key) && <SelectItem value={l.key}>{l.key}</SelectItem>}
              </SelectContent>
            </Select>
            <button data-testid={`sort-up-${i}`} title="Move up" disabled={i === 0} onClick={() => move(i, -1)}
              className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 p-0.5 shrink-0"><ChevronUp className="w-3 h-3" /></button>
            <button data-testid={`sort-down-${i}`} title="Move down" disabled={i === levels.length - 1} onClick={() => move(i, 1)}
              className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 p-0.5 shrink-0"><ChevronDown className="w-3 h-3" /></button>
            <button data-testid={`sort-remove-${i}`} title="Remove level" onClick={() => setLevels(levels.filter((_, j) => j !== i))}
              className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5 shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex items-center gap-1">
            <Select value={l.direction} onValueChange={(v) => patchAt(i, { direction: v })} disabled={!!(l.custom || []).length}>
              <SelectTrigger data-testid={`sort-direction-${i}`} className="bg-[color:var(--surface)] flex-1 min-w-0 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{SORT_DIRECTIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={l.nulls} onValueChange={(v) => patchAt(i, { nulls: v })}>
              <SelectTrigger data-testid={`sort-nulls-${i}`} className="bg-[color:var(--surface)] w-[104px] shrink-0 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{SORT_NULLS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid={`sort-custom-${i}`} size="sm" variant="ghost" title="Custom order"
              className={`h-7 px-2 shrink-0 ${(l.custom || []).length ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)]"}`}
              onClick={() => setCustomAt(i)}><ListOrdered className="w-3.5 h-3.5" /></Button>
          </div>
          {!!(l.custom || []).length && (
            <p className="text-[10px] text-[color:var(--brand-ocean)]">Custom order of {l.custom.length} value{l.custom.length === 1 ? "" : "s"} on {labelOf(l.key)}.</p>
          )}
        </div>
      ))}
      {customAt !== null && (
        <CustomOrderDialog
          open
          level={levels[customAt]}
          label={labelOf(levels[customAt]?.key)}
          config={config}
          datasetId={datasetId}
          onClose={() => setCustomAt(null)}
          onSave={(vals) => { patchAt(customAt, { custom: vals.length ? vals : null }); setCustomAt(null); }}
        />
      )}
    </div>
  );
}

// Manual order of the values of a dimension — list is seeded from the dataset's distinct values.
function CustomOrderDialog({ open, level, label, config, datasetId, onClose, onSave }) {
  const [vals, setVals] = useState(level?.custom || []);
  const [loading, setLoading] = useState(false);
  const field = ((config.dimensions || []).find((d) => d && (d.key || d.alias || d.field) && (d.key || d.alias || d.field) === level?.key) || {}).field
    || (level?.key || "").replace(/_(year|quarter|month|week|day)$/, "");

  const load = async () => {
    if (!datasetId || !field) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/datasets/${datasetId}/distinct`, { field, filters: config.filters || [], limit: 500 });
      const got = smartSortValues((data?.values || data || []).map((v) => (v && typeof v === "object" ? v.value : v)).filter((v) => v !== null && v !== ""), "asc");
      setVals((cur) => [...cur, ...got.map(String).filter((v) => !cur.includes(v))]);
    } catch { /* offline source: the author can still type values */ }
    setLoading(false);
  };

  const move = (i, d) => {
    const to = i + d;
    if (to < 0 || to >= vals.length) return;
    const a = [...vals];
    [a[i], a[to]] = [a[to], a[i]];
    setVals(a);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="sort-custom-dialog" className="max-w-md">
        <DialogHeader><DialogTitle className="text-base">Custom order · {label}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="flex gap-1.5">
            <Button data-testid="sort-custom-load" size="sm" variant="outline" className="h-7 text-xs" disabled={loading || !field} onClick={load}>
              {loading ? "Loading…" : "Load values"}
            </Button>
            <Button data-testid="sort-custom-clear" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setVals([])}>Clear</Button>
          </div>
          <div className="max-h-[320px] overflow-y-auto space-y-1 pr-1">
            {vals.map((v, i) => (
              <div key={`${v}-${i}`} data-testid={`sort-custom-row-${i}`} className="flex items-center gap-1">
                <span className="text-[10px] text-[color:var(--ink-3)] w-5 shrink-0">{i + 1}</span>
                <Input value={v} onChange={(e) => setVals(vals.map((x, j) => (j === i ? e.target.value : x)))} className="h-7 text-xs bg-[color:var(--surface)]" />
                <button data-testid={`sort-custom-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 p-0.5"><ChevronUp className="w-3 h-3" /></button>
                <button data-testid={`sort-custom-down-${i}`} disabled={i === vals.length - 1} onClick={() => move(i, 1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 p-0.5"><ChevronDown className="w-3 h-3" /></button>
                <button data-testid={`sort-custom-del-${i}`} onClick={() => setVals(vals.filter((_, j) => j !== i))} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {!vals.length && <p className="text-[11px] text-[color:var(--ink-3)]">Load the values of this field, or add them one by one, then drag the order with the arrows. Values not listed stay after the listed ones.</p>}
          </div>
          <Button data-testid="sort-custom-add" size="sm" variant="ghost" className="h-7 text-xs text-[color:var(--brand-ocean)]" onClick={() => setVals([...vals, ""])}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add value
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="h-8 text-xs">Cancel</Button>
          <Button data-testid="sort-custom-save" onClick={() => onSave(vals.map((v) => String(v).trim()).filter(Boolean))} className="h-8 text-xs">Apply order</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
